import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import Magnetic from "./Magnetic";

export default function Navbar() {
  const { tr, lang, toggle, isAr } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: tr.nav.home },
    { to: "/about", label: tr.nav.about },
    { to: "/products", label: tr.nav.products },
    { to: "/vision", label: tr.nav.vision },
    { to: "/contact", label: tr.nav.contact },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav border-b border-border/60 shadow-[0_8px_30px_-12px_rgba(16,24,40,0.12)]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 h-16 sm:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/assets/logo.png" alt="ICON Trading Co. logo" className="h-9 w-9 sm:h-11 sm:w-11 object-contain transition-transform duration-500 group-hover:rotate-[8deg]" />
          <span className="flex flex-col leading-none">
            <span className="font-grotesk text-base sm:text-lg font-700 tracking-tight text-ink">ICON</span>
            <span className="text-[9px] sm:text-[10px] font-600 tracking-ultra text-brand-blue uppercase">Trading Co.</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-500 rounded-full transition-colors ${
                  isActive ? "text-brand-blue" : "text-slate hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span layoutId="nav-pill" className="absolute inset-0 -z-10 rounded-full bg-brand-blue/10" transition={{ type: "spring", stiffness: 380, damping: 30 }} />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 rounded-full border border-border bg-white/70 px-3 py-2 text-xs font-600 text-ink transition-all hover:border-brand-blue hover:text-brand-blue"
            aria-label="Toggle language"
          >
            <Globe className="h-3.5 w-3.5" />
            {tr.nav.langLabel}
          </button>

          <Magnetic className="hidden sm:block" strength={0.35}>
            <button
              onClick={() => navigate("/contact")}
              data-testid="nav-cta"
              className="group flex items-center gap-1.5 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-600 text-white shadow-lg shadow-brand-blue/25 transition-all hover:bg-brand-blue-dark"
            >
              {tr.nav.cta}
              <ArrowUpRight className={`h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${isAr ? "rotate-90" : ""}`} />
            </button>
          </Magnetic>

          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" className="lg:hidden rounded-full border border-border bg-white/70 p-2.5 text-ink" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass-nav border-t border-border/60 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) => `py-3 text-base font-500 border-b border-border/50 ${isActive ? "text-brand-blue" : "text-ink"}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <button onClick={() => navigate("/contact")} className="mt-4 rounded-full bg-brand-blue px-5 py-3 text-sm font-600 text-white">
                {tr.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
